import './Scorecard.scss';
import { ReactElement, useMemo, useState } from 'react';
import {
  MdAutoAwesome,
  MdInsights,
  MdMenuBook,
  MdOutlineFileDownload,
  MdRefresh,
  MdTextFields
} from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { SegmentedControlButton } from '../../../Components/SegmentedControlButton';
import { Appearance, Design, Size } from '../../../Components/designConstants';
import {
  AlexaAssessment,
  AlexaAssessmentStore,
  clearAssessmentStore,
  readAssessmentStore,
  writeAssessmentStore
} from './alexa-assessments-utils';
import { AlexaReadinessModal } from './AlexaReadinessModal';
import { AssessReadinessModal } from './AssessReadinessModal';
import { BatchOptimizeModal } from './BatchOptimizeModal';
import { GuidanceCenter } from './GuidanceCenter';
import { OptimizeModal } from './OptimizeModal';
import {
  buildProposalsCSV,
  clearProposalStore,
  getEffectiveProduct,
  ProductProposal,
  ProposalStore,
  pruneEmptyProposals,
  readProposalStore,
  writeProposalStore
} from './proposals-utils';
import { ScorecardHealthWorkspace } from './ScorecardHealthWorkspace';
import { ScorecardImportPanel } from './ScorecardImportPanel';
import { DataMode, ScorecardWorkspace, ScorecardWorkspaceType } from './scorecard-constants';
import {
  clearPersistedScorecardState,
  readPersistedScorecardState,
  writePersistedScorecardState
} from './scorecard-utils';
import { AuditedProduct } from './scoring-engine-constants';
import { TitleComplianceView } from './TitleComplianceView';
import { writeTitleRewriteStore } from './title-compliance-utils';

const WORKSPACE_OPTIONS: {
  key: ScorecardWorkspaceType;
  label: string;
  renderLabel: (label: string) => ReactElement;
}[] = [
  {
    key: ScorecardWorkspace.HEALTH,
    label: 'Portfolio Health',
    renderLabel: label => (
      <span className='scorecard-workspace-option'>
        <MdInsights /> {label}
      </span>
    )
  },
  {
    key: ScorecardWorkspace.TITLES,
    label: 'Title Compliance',
    renderLabel: label => (
      <span className='scorecard-workspace-option'>
        <MdTextFields /> {label}
      </span>
    )
  }
];

interface ScorecardPanelProps {
  tld: string;
}

export const ScorecardPanel = ({ tld }: ScorecardPanelProps) => {
  const [products, setProducts] = useState<AuditedProduct[]>(
    () => readPersistedScorecardState()?.products ?? []
  );
  const [dataMode, setDataMode] = useState(
    () => readPersistedScorecardState()?.mode ?? DataMode.EMPTY
  );
  const [workspace, setWorkspace] = useState<ScorecardWorkspaceType>(ScorecardWorkspace.HEALTH);
  const [guidanceOpen, setGuidanceOpen] = useState(false);

  const [proposals, setProposals] = useState<ProposalStore>(() => readProposalStore());
  const [assessments, setAssessments] = useState<AlexaAssessmentStore>(() => readAssessmentStore());

  const [optimizingProduct, setOptimizingProduct] = useState<AuditedProduct | null>(null);
  const [batchOptimizeOpen, setBatchOptimizeOpen] = useState(false);
  const [assessingProduct, setAssessingProduct] = useState<AuditedProduct | null>(null);
  const [assessBatchOpen, setAssessBatchOpen] = useState(false);
  // Bumped on every fresh import so TitleComplianceView remounts (and re-reads its
  // now-cleared title-rewrite store) instead of carrying stale per-product-id state
  // forward under IDs that no longer correspond to the newly loaded catalog.
  const [datasetVersion, setDatasetVersion] = useState(0);

  const handleProductsLoaded = (loaded: AuditedProduct[], fileName: string) => {
    setProducts(loaded);
    setDataMode(DataMode.LIVE);
    writePersistedScorecardState({ version: 1, mode: DataMode.LIVE, fileName, products: loaded });
    // Proposals/assessments/title-rewrites are keyed by product id, and CSV imports
    // mint a fresh random id on every parse — without this, re-importing the same
    // (or a different) dataset strands prior entries under unreachable ids and the
    // three localStorage keys grow without bound across a long session.
    setProposals({});
    setAssessments({});
    clearProposalStore();
    clearAssessmentStore();
    writeTitleRewriteStore({});
    setDatasetVersion(v => v + 1);
  };

  const handleClearData = () => {
    setProducts([]);
    setDataMode(DataMode.EMPTY);
    setProposals({});
    setAssessments({});
    clearPersistedScorecardState();
    clearProposalStore();
    clearAssessmentStore();
    writeTitleRewriteStore({});
    setDatasetVersion(v => v + 1);
  };

  const handleSaveProposal = (productId: string, proposal: ProductProposal | null) => {
    setProposals(prev => {
      const next = { ...prev };
      if (proposal === null) {
        delete next[productId];
      } else {
        next[productId] = proposal;
      }
      const pruned = pruneEmptyProposals(next);
      writeProposalStore(pruned);
      return pruned;
    });
  };

  const handleSaveAssessment = (productId: string, assessment: AlexaAssessment | null) => {
    setAssessments(prev => {
      const next = { ...prev };
      if (assessment === null) {
        delete next[productId];
      } else {
        next[productId] = assessment;
      }
      writeAssessmentStore(next);
      return next;
    });
  };

  const effectiveProducts = useMemo(
    () => products.map(p => getEffectiveProduct(p, proposals[p.id])),
    [products, proposals]
  );

  const handleExportProposals = () => {
    const csv = buildProposalsCSV(products, proposals);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai_rewrites_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const hasProposals = Object.keys(proposals).length > 0;

  return (
    <div className='scorecard-panel'>
      <ScorecardImportPanel tld={tld} onProductsLoaded={handleProductsLoaded} />

      {dataMode !== DataMode.EMPTY && (
        <>
          <div className='scorecard-panel-toolbar'>
            <SegmentedControlButton
              options={WORKSPACE_OPTIONS}
              selectedOption={workspace}
              onChange={key => setWorkspace(key)}
            />
            <div className='scorecard-panel-actions'>
              {workspace === ScorecardWorkspace.HEALTH && (
                <>
                  {/* Primary action for the Health workspace */}
                  <Button
                    size={Size.SM}
                    leadingElement={<MdAutoAwesome />}
                    onClick={() => setBatchOptimizeOpen(true)}
                  >
                    Batch Optimize
                  </Button>
                  <Button
                    appearance={Appearance.OUTLINED}
                    size={Size.SM}
                    leadingElement={<MdAutoAwesome />}
                    onClick={() => setAssessBatchOpen(true)}
                  >
                    Assess Readiness
                  </Button>
                  {hasProposals && (
                    <Button
                      appearance={Appearance.OUTLINED}
                      size={Size.SM}
                      leadingElement={<MdOutlineFileDownload />}
                      onClick={handleExportProposals}
                    >
                      Export AI Rewrites
                    </Button>
                  )}
                  <span className='scorecard-toolbar-divider' />
                  {/* Scoring Weights documents the Health rubric — Health-only */}
                  <Button
                    appearance={Appearance.EMPTY}
                    design={Design.NEUTRAL}
                    size={Size.SM}
                    leadingElement={<MdMenuBook />}
                    onClick={() => setGuidanceOpen(true)}
                  >
                    Scoring Weights
                  </Button>
                </>
              )}
              <Button
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                size={Size.SM}
                leadingElement={<MdRefresh />}
                onClick={handleClearData}
              >
                Clear Data
              </Button>
            </div>
          </div>

          {workspace === ScorecardWorkspace.HEALTH ? (
            <ScorecardHealthWorkspace
              products={products}
              proposals={proposals}
              assessments={assessments}
              tld={tld}
              onOptimize={setOptimizingProduct}
              onAssess={setAssessingProduct}
            />
          ) : (
            <TitleComplianceView key={datasetVersion} products={products} />
          )}
        </>
      )}

      {optimizingProduct && (
        <OptimizeModal
          product={optimizingProduct}
          existingProposal={proposals[optimizingProduct.id]}
          onSaveProposal={handleSaveProposal}
          onClose={() => setOptimizingProduct(null)}
        />
      )}
      {batchOptimizeOpen && (
        <BatchOptimizeModal
          products={products}
          proposals={proposals}
          onSaveProposal={handleSaveProposal}
          onClose={() => setBatchOptimizeOpen(false)}
        />
      )}
      {assessingProduct && (
        <AlexaReadinessModal
          product={assessingProduct}
          existingAssessment={assessments[assessingProduct.id]}
          existingProposal={proposals[assessingProduct.id]}
          onSaveAssessment={handleSaveAssessment}
          onSaveProposal={handleSaveProposal}
          onClose={() => setAssessingProduct(null)}
        />
      )}
      {assessBatchOpen && (
        <AssessReadinessModal
          products={effectiveProducts}
          assessments={assessments}
          onSaveAssessment={handleSaveAssessment}
          onClose={() => setAssessBatchOpen(false)}
        />
      )}
      {guidanceOpen && <GuidanceCenter onClose={() => setGuidanceOpen(false)} />}
    </div>
  );
};
